import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Warning } from 'phosphor-react';
import { toast } from 'sonner';
import { centralApi, IEmpresaCarteira, IPermissaoGeral } from '../../hooks/useCentralPermissoes';
import { EmpresasAvulsas } from './EmpresasAvulsas';
import { CODIGO_VER_TODAS_CARTEIRAS, DESCRICAO_GERAL, geralVisivel } from './modulos';
import { AZUL, BarraSalvar, Busca, Cartao, Contador, Interruptor, TEXTO, TEXTO_SUAVE } from './ui';

const iguais = (a: string[] | number[], b: string[] | number[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

/** Liga/desliga uma permissão geral do grupo preservando as demais. */
async function gravarGerais(grupoId: number, ids: number[]) {
  await centralApi.salvarGeraisDoGrupo(grupoId, Array.from(new Set(ids)));
}

export function GrupoCarteira({ grupoId, grupoNome }: { grupoId: number; grupoNome: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const [marcados, setMarcados] = useState<string[]>([]);
  const [todas, setTodas] = useState(false);
  const [avulsas, setAvulsas] = useState<IEmpresaCarteira[]>([]);

  const { data: disponiveis = [], isLoading: l1 } = useQuery({
    queryKey: ['central', 'carteiras-disponiveis'],
    queryFn: centralApi.carteirasDisponiveis,
  });
  const { data: doGrupo, isLoading: l2 } = useQuery({
    queryKey: ['central', 'grupo-carteira', grupoId],
    queryFn: () => centralApi.carteiraDoGrupo(grupoId),
  });
  const { data: gerais = [], isLoading: l3 } = useQuery({
    queryKey: ['central', 'grupo-gerais', grupoId],
    queryFn: () => centralApi.geraisDoGrupo(grupoId),
  });
  const { data: catalogoGerais = [] } = useQuery({
    queryKey: ['central', 'permissoes-gerais'],
    queryFn: centralApi.permissoesGerais,
  });
  const { data: avulsasDoGrupo, isError: semRotaDeAvulsas } = useQuery({
    queryKey: ['central', 'grupo-empresas', grupoId],
    queryFn: () => centralApi.empresasDoGrupo(grupoId),
    retry: false,
  });
  useEffect(() => setAvulsas(avulsasDoGrupo ?? []), [avulsasDoGrupo]);
  const idsAvulsasBase = (avulsasDoGrupo ?? []).map(e => e.id);
  const mudouAvulsas = !iguais(
    avulsas.map(e => e.id),
    idsAvulsasBase,
  );

  const todasNaBase = gerais.some(p => p.codigo === CODIGO_VER_TODAS_CARTEIRAS);
  useEffect(() => setMarcados(doGrupo ?? []), [doGrupo]);
  useEffect(() => setTodas(todasNaBase), [todasNaBase, grupoId]);

  const mudou = !iguais(marcados, doGrupo ?? []) || todas !== todasNaBase || mudouAvulsas;
  const filtrados = disponiveis.filter(g => g.toLowerCase().includes(busca.trim().toLowerCase()));

  const salvar = useMutation({
    mutationFn: async () => {
      if (!iguais(marcados, doGrupo ?? []))
        await centralApi.salvarCarteiraDoGrupo(grupoId, marcados);
      if (mudouAvulsas)
        await centralApi.salvarEmpresasDoGrupo(
          grupoId,
          avulsas.map(e => e.id),
        );
      if (todas !== todasNaBase) {
        const permissao = catalogoGerais.find(p => p.codigo === CODIGO_VER_TODAS_CARTEIRAS);
        if (!permissao) throw new Error('permissão de ver todas as carteiras não existe');
        const atuais = gerais.map(p => p.id).filter(id => id !== permissao.id);
        await gravarGerais(grupoId, todas ? [...atuais, permissao.id] : atuais);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      toast.success(`Carteira de ${grupoNome} salva`);
    },
    onError: () => toast.error('Não foi possível salvar a carteira'),
  });

  if (l1 || l2 || l3) {
    return (
      <Flex justify="center" py={16}>
        <Spinner color={AZUL} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="14px">
        <Flex justify="space-between" align="flex-start" gap="10px" wrap="wrap">
          <Box>
            <Text fontSize="16px" fontWeight={650} color={TEXTO}>
              Carteira de empresas
            </Text>
            <Text fontSize="13px" color={TEXTO_SUAVE}>
              Grupos de empresas e empresas avulsas que os usuários deste grupo enxergam em todo o
              sistema.
            </Text>
          </Box>
          <Contador>
            {marcados.length} de {disponiveis.length}
          </Contador>
        </Flex>

        <Flex
          align="center"
          gap="12px"
          p="12px 14px"
          borderRadius="14px"
          bg="rgba(248,250,252,.85)"
        >
          <Interruptor
            ligado={todas}
            onChange={() => setTodas(v => !v)}
            rotulo="Ver todas as carteiras"
          />
          <Box>
            <Text fontSize="13.5px" fontWeight={700}>
              Ver todas as carteiras
            </Text>
            <Text fontSize="12px" color={TEXTO_SUAVE}>
              Ligada, a lista abaixo não vale: o grupo vê todas as empresas.
            </Text>
          </Box>
        </Flex>

        <Flex gap="10px" align="center" wrap="wrap">
          <Box flex="1 1 240px" maxW="340px">
            <Busca valor={busca} onChange={setBusca} placeholder="Buscar grupo de empresas" />
          </Box>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate('/configuracoes/permissoes?modo=empresas')}
          >
            Gerir grupos de empresas
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="8px" opacity={todas ? 0.55 : 1}>
          {filtrados.map(g => {
            const on = marcados.includes(g);
            return (
              <Box
                as="label"
                key={g}
                display="flex"
                alignItems="center"
                gap="10px"
                p="10px 12px"
                borderRadius="14px"
                fontSize="13px"
                fontWeight={600}
                color="#334155"
                cursor="pointer"
                bg={on ? 'rgba(223,243,255,.8)' : 'rgba(255,255,255,.65)'}
                boxShadow={
                  on ? 'inset 0 0 0 1px rgba(0,119,182,.2)' : 'inset 0 0 0 1px rgba(15,23,42,.06)'
                }
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() =>
                    setMarcados(prev => (on ? prev.filter(x => x !== g) : [...prev, g]))
                  }
                  style={{ width: 18, height: 18, accentColor: AZUL }}
                />
                {g}
              </Box>
            );
          })}
        </SimpleGrid>

        <Box borderTop="1px solid rgba(15,23,42,.07)" pt="14px" opacity={todas ? 0.55 : 1}>
          <>
            <EmpresasAvulsas selecionadas={avulsas} onChange={setAvulsas} />
            {semRotaDeAvulsas && (
              <Text fontSize="12.5px" color="#92400E">
                Pré-visualização: a busca já funciona, mas salvar empresas avulsas depende do
                backend da carteira por empresa, que ainda não está nesta versão.
              </Text>
            )}
          </>
        </Box>

        {!todas && marcados.length === 0 && avulsas.length === 0 && (
          <Flex
            gap="8px"
            align="flex-start"
            fontSize="12.5px"
            color="#92400E"
            bg="#FEFCE8"
            p="10px 12px"
            borderRadius="14px"
            boxShadow="inset 0 0 0 1px #FDE68A"
          >
            <Warning size={16} />
            <Text>
              Nenhum grupo de empresas nem empresa avulsa marcados e “Ver todas” desligado: o grupo
              não vê nenhuma empresa.
            </Text>
          </Flex>
        )}
      </Cartao>

      <BarraSalvar
        texto={
          mudou
            ? `Carteira de ${grupoNome} alterada. Só é gravada ao salvar.`
            : 'Nenhuma alteração pendente.'
        }
        semMudanca={!mudou}
        salvando={salvar.isPending}
        onDesfazer={() => {
          setMarcados(doGrupo ?? []);
          setTodas(todasNaBase);
          setAvulsas(avulsasDoGrupo ?? []);
        }}
        onSalvar={() => salvar.mutate()}
      />
    </Flex>
  );
}

export function GrupoGerais({ grupoId, grupoNome }: { grupoId: number; grupoNome: string }) {
  const queryClient = useQueryClient();
  const [ligadas, setLigadas] = useState<number[]>([]);

  const { data: catalogo = [], isLoading: l1 } = useQuery({
    queryKey: ['central', 'permissoes-gerais'],
    queryFn: centralApi.permissoesGerais,
  });
  const { data: doGrupo, isLoading: l2 } = useQuery({
    queryKey: ['central', 'grupo-gerais', grupoId],
    queryFn: () => centralApi.geraisDoGrupo(grupoId),
  });

  const visiveis = useMemo(() => catalogo.filter(p => geralVisivel(p.codigo)), [catalogo]);
  const base = useMemo(() => (doGrupo ?? []).map(p => p.id), [doGrupo]);
  useEffect(() => setLigadas(base), [base]);
  const mudou = !iguais(ligadas, base);

  const salvar = useMutation({
    mutationFn: () => gravarGerais(grupoId, ligadas),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      toast.success(`Permissões gerais de ${grupoNome} salvas`);
    },
    onError: () => toast.error('Não foi possível salvar as permissões gerais'),
  });

  if (l1 || l2) {
    return (
      <Flex justify="center" py={16}>
        <Spinner color={AZUL} />
      </Flex>
    );
  }

  const linha = (p: IPermissaoGeral) => {
    const on = ligadas.includes(p.id);
    return (
      <Flex
        key={p.id}
        align="center"
        gap="12px"
        p="10px 4px"
        borderBottom="1px solid rgba(15,23,42,.05)"
      >
        <Interruptor
          ligado={on}
          rotulo={p.nome}
          onChange={() => setLigadas(prev => (on ? prev.filter(x => x !== p.id) : [...prev, p.id]))}
        />
        <Box flex={1} minW={0}>
          <Text fontSize="13.5px" fontWeight={700}>
            {p.nome}
          </Text>
          <Text fontSize="12px" color={TEXTO_SUAVE}>
            {DESCRICAO_GERAL[p.codigo] ?? p.codigo}
          </Text>
        </Box>
      </Flex>
    );
  };

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px">
        <Text fontSize="16px" fontWeight={650} color={TEXTO}>
          Permissões gerais
        </Text>
        <Text fontSize="13px" color={TEXTO_SUAVE} mb="8px">
          Liberações que não são de uma tela.
        </Text>
        {visiveis.map(linha)}
        {visiveis.length === 0 && (
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            Nenhuma permissão geral cadastrada.
          </Text>
        )}
      </Cartao>
      <BarraSalvar
        texto={
          mudou
            ? `Permissões gerais de ${grupoNome} alteradas. Só são gravadas ao salvar.`
            : 'Nenhuma alteração pendente.'
        }
        semMudanca={!mudou}
        salvando={salvar.isPending}
        onDesfazer={() => setLigadas(base)}
        onSalvar={() => salvar.mutate()}
      />
    </Flex>
  );
}
